import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../api';
import RightSidebar from './RightSidebar';
import OdometerForm from './OdometerForm';
import { Search, Download } from 'lucide-react';


const OdometerTable = ({ readings, onAddReading, searchTerm, setSearchTerm, fromDate, setFromDate, toDate, setToDate, onDownloadCsv }) => (
  <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl p-6 sm:p-8">
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-white">
          Vehicle Odometer Readings
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Showing the latest reading for each vehicle per day.
        </p>
      </div>
      <button
        onClick={onAddReading}
        className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
      >
        Add Manual Reading
      </button>
    </div>


    {/* Filter and Action Controls */}
    <div className="flex flex-col sm:flex-row flex-wrap items-center justify-start gap-4 mb-6">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by vehicle no..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full sm:w-auto"
          />
        </div>
        
        {/* Date Filters */}
        <div className="flex items-center gap-2">
            <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <span className="text-gray-500 dark:text-gray-400">to</span>
            <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                min={fromDate}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
        </div>


        {/* Download Button */}
        <button
            onClick={onDownloadCsv}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
        >
            <Download className="w-5 h-5" />
            <span>Download CSV</span>
        </button>
    </div>


    <div className="overflow-x-auto">
      <table className="min-w-full leading-normal">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-700">
            <th className="px-6 py-4 border-b-2 border-gray-200 dark:border-gray-600 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
              Vehicle No
            </th>
            <th className="px-6 py-4 border-b-2 border-gray-200 dark:border-gray-600 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
              Reading Date
            </th>
            <th className="px-6 py-4 border-b-2 border-gray-200 dark:border-gray-600 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
              Start Odometer
            </th>
            <th className="px-6 py-4 border-b-2 border-gray-200 dark:border-gray-600 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
              End Odometer
            </th>
            <th className="px-6 py-4 border-b-2 border-gray-200 dark:border-gray-600 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
              Running KM
            </th>
            <th className="px-6 py-4 border-b-2 border-gray-200 dark:border-gray-600 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
              Type
            </th>
          </tr>
        </thead>
        <tbody>
          {readings.map((reading, index) => (
            <tr
              key={`${reading.id}-${index}`}
              className="hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              <td className="px-6 py-5 border-b border-gray-200 dark:border-gray-600 text-sm">
                <p className="text-gray-900 dark:text-white whitespace-no-wrap font-medium">
                  {reading.vehicle_no}
                </p>
              </td>
              <td className="px-6 py-5 border-b border-gray-200 dark:border-gray-600 text-sm">
                <p className="text-gray-900 dark:text-white whitespace-no-wrap">
                  {new Date(reading.reading_date).toLocaleDateString()}
                </p>
              </td>
              <td className="px-6 py-5 border-b border-gray-200 dark:border-gray-600 text-sm">
                <p className="text-gray-900 dark:text-white whitespace-no-wrap font-medium">
                  {reading.start_odo_meter?? '-'}
                </p>
              </td>
              <td className="px-6 py-5 border-b border-gray-200 dark:border-gray-600 text-sm">
                <p className="text-gray-900 dark:text-white whitespace-no-wrap font-medium">
                  {reading.end_odo_meter?? '-'}
                </p>
              </td>
              <td className="px-6 py-5 border-b border-gray-200 dark:border-gray-600 text-sm">
                <p className="text-gray-900 dark:text-white whitespace-no-wrap">
                  {parseFloat(reading.running_km).toFixed(2)} km
                </p>
              </td>
              <td className="px-6 py-5 border-b border-gray-200 dark:border-gray-600 text-sm">
                <span
                  className={`relative inline-block px-3 py-1 font-semibold leading-tight rounded-full ${
                    reading.reading_type === 'Auto'
                      ? 'text-green-900 bg-green-200 dark:text-green-100 dark:bg-green-700'
                      : 'text-blue-900 bg-blue-200 dark:text-blue-100 dark:bg-blue-700'
                  }`}
                >
                  {reading.reading_type}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);



const OdometerView = () => {
  const [readings, setReadings] = useState([]);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');



  const fetchReadings = useCallback(async () => {
    try {
      const response = await api.get('/odometer/all-readings');
      setReadings(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching odometer readings:', error);
      setReadings([]); // Set to empty on error
    }
  }, []);



  useEffect(() => {
    fetchReadings();
  }, [fetchReadings]);



  const handleOdometerAdded = () => {
    fetchReadings();
    setSidebarOpen(false);
  };



  // MODIFIED: Filter logic to show only the latest reading per vehicle per day.
  // const filteredReadings = useMemo(() => {
  //   // 1. First, apply the search and date range filters
  //   const initialFilter = readings.filter(reading => {
  //       const searchMatch = reading.vehicle_no.toLowerCase().includes(searchTerm.toLowerCase());

  //       if (!fromDate && !toDate) {
  //           return searchMatch;
  //       }
        
  //       if (!reading.reading_date) return false;

  //       const readingDate = new Date(reading.reading_date);
  //       const start = fromDate ? new Date(fromDate) : null;
  //       const end = toDate ? new Date(toDate) : null;

  //       if (start) start.setUTCHours(0, 0, 0, 0);
  //       if (end) end.setUTCHours(23, 59, 59, 999);

  //       const dateMatch = (!start || readingDate >= start) && (!end || readingDate <= end);

  //       return searchMatch && dateMatch;
  //   });

  //   // 2. Group by vehicle and day, keeping only the latest reading
  //   const latestReadingsMap = new Map();

  //   initialFilter.forEach(reading => {
  //       const dateKey = new Date(reading.reading_date).toISOString().split('T')[0]; // 'YYYY-MM-DD'
  //       const compositeKey = `${reading.vehicle_no}|${dateKey}`;

  //       const existingReading = latestReadingsMap.get(compositeKey);

  //       // If no reading exists for this vehicle on this day, or if the current one is newer, add/update it
  //       if (!existingReading || new Date(reading.reading_date) > new Date(existingReading.reading_date)) {
  //           latestReadingsMap.set(compositeKey, reading);
  //       }
  //   });

  //   // 3. Convert the map values back to an array and sort for consistent display
  //   const uniqueLatestReadings = Array.from(latestReadingsMap.values());

  //   uniqueLatestReadings.sort((a, b) => {
  //       const dateA = new Date(a.reading_date);
  //       const dateB = new Date(b.reading_date);
  //       if (dateB - dateA !== 0) {
  //           return dateB - dateA; // Sort by date descending (most recent first)
  //       }
  //       // If dates are the same, sort by vehicle number for consistency
  //       return a.vehicle_no.localeCompare(b.vehicle_no);
  //   });

  //   return uniqueLatestReadings;

  // }, [readings, searchTerm, fromDate, toDate]);
    const filteredReadings = useMemo(() => {
  // Step 1: Apply search and date filters
  const initialFilter = readings.filter((reading) => {
    const searchMatch = reading.vehicle_no
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());

    if (!fromDate && !toDate) return searchMatch;

    if (!reading.reading_date) return false;

    const readingDate = new Date(reading.reading_date);
    const start = fromDate ? new Date(fromDate) : null;
    const end = toDate ? new Date(toDate) : null;

    if (start) start.setUTCHours(0, 0, 0, 0);
    if (end) end.setUTCHours(23, 59, 59, 999);

    const dateMatch =
      (!start || readingDate >= start) && (!end || readingDate <= end);

    return searchMatch && dateMatch;
  });

  // Step 2: Group by vehicle and date (keep latest per day)
  const latestReadingsMap = new Map();
  initialFilter.forEach((reading) => {
    const dateKey = new Date(reading.reading_date).toISOString().split("T")[0];
    const compositeKey = `${reading.vehicle_no}|${dateKey}`;
    const existingReading = latestReadingsMap.get(compositeKey);

    if (
      !existingReading ||
      new Date(reading.reading_date) > new Date(existingReading.reading_date)
    ) {
      latestReadingsMap.set(compositeKey, reading);
    }
  });

  // Step 3: Convert to array
  const uniqueLatestReadings = Array.from(latestReadingsMap.values());

  // Step 4: Replace zero readings with previous valid non-zero for same vehicle
  const byVehicle = uniqueLatestReadings.reduce((acc, reading) => {
    const vno = reading.vehicle_no;
    if (!acc[vno]) acc[vno] = [];
    acc[vno].push(reading);
    return acc;
  }, {});

  Object.values(byVehicle).forEach((vehicleReadings) => {
    // Sort ascending by date to propagate old → new
    vehicleReadings.sort(
      (a, b) => new Date(a.reading_date) - new Date(b.reading_date)
    );

    let lastValidStart = null;
    let lastValidEnd = null;

    for (const r of vehicleReadings) {
      if (r.start_odo_meter && r.start_odo_meter !== 0)
        lastValidStart = r.start_odo_meter;
      else if (lastValidStart !== null) r.start_odo_meter = lastValidStart;

      if (r.end_odo_meter && r.end_odo_meter !== 0)
        lastValidEnd = r.end_odo_meter;
      else if (lastValidEnd !== null) r.end_odo_meter = lastValidEnd;
    }
  });

  // Step 5: Flatten again and sort for display (newest first)
  const correctedReadings = Object.values(byVehicle).flat();

  correctedReadings.sort((a, b) => {
    const dateA = new Date(a.reading_date);
    const dateB = new Date(b.reading_date);
    if (dateB - dateA !== 0) return dateB - dateA;
    return a.vehicle_no.localeCompare(b.vehicle_no);
  });

  return correctedReadings;
}, [readings, searchTerm, fromDate, toDate]);

  // Handler for CSV Download (no changes needed, it uses the processed list)
  const handleDownloadCsv = () => {
    if (filteredReadings.length === 0) {
      alert("No data to download.");
      return;
    }


    const headers = ['Vehicle No', 'Reading Date','Start Odometer', 'End Odometer', 'Running KM', 'Type'];
    
    const formatCsvCell = (cellData) => {
      const stringData = String(cellData ?? '');
      if (stringData.includes(',') || stringData.includes('"') || stringData.includes('\n')) {
        return `"${stringData.replace(/"/g, '""')}"`;
      }
      return stringData;
    };


    const csvRows = [
      headers.join(','),
      ...filteredReadings.map(reading => [
        formatCsvCell(reading.vehicle_no),
        formatCsvCell(new Date(reading.reading_date).toLocaleDateString()),
        formatCsvCell(reading.start_odo_meter ?? ''),
        formatCsvCell(reading.end_odo_meter ?? ''),
        formatCsvCell(parseFloat(reading.running_km).toFixed(2)),
        formatCsvCell(reading.reading_type)
      ].join(','))
    ];


    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `odometer-readings-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };



  return (
    <div className="h-full">
      <OdometerTable
        readings={filteredReadings}
        onAddReading={() => setSidebarOpen(true)}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        fromDate={fromDate}
        setFromDate={setFromDate}
        toDate={toDate}
        setToDate={setToDate}
        onDownloadCsv={handleDownloadCsv}
      />
      <RightSidebar
        isOpen={isSidebarOpen}
        onClose={() => setSidebarOpen(false)}
      >
        <OdometerForm onOdometerAdded={handleOdometerAdded} />
      </RightSidebar>
    </div>
  );
};



export default OdometerView;
